import { useQuery } from '@tanstack/react-query';
import { integrationsApi } from 'src/services';
import { UseQueryOverrideOptions } from '../common';

export type SuperAdminEhrAppointment = {
  appointments_id: number | string;
  appointment_status: string;
  appointment_start_time: string;
  appointment_created_time: string;
  appointment_type: string | number;
  provider_id: number | string;
  patient_id: number | string;
  provider_name?: string;
  patient_name?: string;
  gender?: string;
  patient_email?: string;
  birth_date?: string;
  phone?: string;
};

export type SuperAdminEhrAppointmentsResponse = {
  success: boolean;
  code: number;
  appointments: SuperAdminEhrAppointment[];
  meta: {
    org_uuid: string;
    ehr_system: string;
    provider_id: string;
    date: string;
    count: number;
  };
  message?: string;
};

export type UseFetchSuperAdminEhrAppointmentsParams = {
  org_uuid: string;
  ehr_system: string;
  provider_id: string;
  date: string;
  practice_id?: string;
  department_id?: string;
};

export const useFetchSuperAdminEhrAppointments = (
  params: UseFetchSuperAdminEhrAppointmentsParams,
  options: UseQueryOverrideOptions = {}
) => {
  const {
    org_uuid,
    ehr_system,
    provider_id,
    date,
    practice_id,
    department_id,
  } = params;

  return useQuery({
    queryKey: ['superAdminEhrAppointments', params],
    queryFn: () =>
      integrationsApi
        .get<SuperAdminEhrAppointmentsResponse>(
          '/v2/superadmin/ehr-appointments',
          {
            params: {
              org_uuid,
              ehr_system,
              provider_id,
              date,
              practice_id,
              department_id,
            },
            headers: {
              'Headless-Mode': 'true',
              'Impersonate-Org-UUID': org_uuid,
            },
          }
        )
        .then((res) => res.data),
    enabled: Boolean(org_uuid && ehr_system && provider_id && date),
    refetchOnWindowFocus: false,
    ...options,
  });
};

export type SuperAdminEhrProvider = {
  id: string | number;
  name: string;
  profiles?: Array<{
    id: string | number;
    profile_code: string;
    description: string;
  }>;
};

export type SuperAdminEhrProvidersResponse = {
  success: boolean;
  code: number;
  providers: SuperAdminEhrProvider[];
  meta: {
    org_uuid: string;
    ehr_system: string;
    count: number;
  };
  message?: string;
};

export type UseFetchSuperAdminEhrProvidersParams = {
  org_uuid: string;
  ehr_system: string;
  practice_id?: string;
};

export const useFetchSuperAdminEhrProviders = (
  params: UseFetchSuperAdminEhrProvidersParams,
  options: UseQueryOverrideOptions = {}
) => {
  const { org_uuid, ehr_system, practice_id } = params;

  return useQuery({
    queryKey: ['superAdminEhrProviders', params],
    queryFn: () =>
      integrationsApi
        .get<SuperAdminEhrProvidersResponse>('/v2/superadmin/ehr-providers', {
          params: {
            org_uuid,
            ehr_system,
            practice_id,
          },
          headers: {
            'Headless-Mode': 'true',
            'Impersonate-Org-UUID': org_uuid,
          },
        })
        .then((res) => res.data),
    enabled: Boolean(org_uuid && ehr_system),
    refetchOnWindowFocus: false,
    ...options,
  });
};
